import React, { useEffect, useState } from "react";
import { Loader2, PlayCircle, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * CustomPortfolioPicker — subscriber-only tool on /portfolio.
 * Pick any subset of the last-N-days approved alerts and re-run the
 * same simulation math to see "what would MY picks have delivered".
 * Explicitly labeled "custom scenario, not the published number".
 *
 * Picks are persisted on the user account (POST /portfolio/custom
 * saves them, GET /portfolio/custom/saved rehydrates), so a returning
 * user sees the same boxes checked AND the same custom result driving
 * the hero cards + curve chart above (via `onResult` callback).
 */
export default function CustomPortfolioPicker({ user, onResult }) {
  const [candidates, setCandidates] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [hydrating, setHydrating] = useState(true);

  const isSubscriber = user && (user.role === "admin" || user.role === "subscriber" || user.subscribed === true);

  // Load candidates + any previously-saved picks in parallel.
  useEffect(() => {
    if (!isSubscriber) { setHydrating(false); return; }
    let cancelled = false;
    Promise.all([
      api.get("/portfolio/candidates?days=90").then((r) => r.data.candidates || []).catch(() => []),
      api.get("/portfolio/custom/saved").then((r) => r.data).catch(() => ({ saved: null })),
    ]).then(([cands, saved]) => {
      if (cancelled) return;
      setCandidates(cands);
      if (saved && saved.saved && Array.isArray(saved.saved.alert_ids) && saved.saved.alert_ids.length > 0) {
        setPicked(new Set(saved.saved.alert_ids));
        if (saved.result) {
          setResult(saved.result);
          onResult && onResult(saved.result);
        }
      }
    }).finally(() => { if (!cancelled) setHydrating(false); });
    return () => { cancelled = true; };
  }, [isSubscriber]);

  if (!isSubscriber) {
    return (
      <div className="mt-6 p-5 rounded-lg border border-dashed border-mb-border text-sm text-neutral-500" data-testid="custom-scenario-locked">
        Subscribers can hand-pick a custom set of alerts and re-run the simulation. <a href="/#pricing" className="text-mb-emerald underline">Activate a plan →</a>
      </div>
    );
  }

  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };

  const runSim = async () => {
    if (picked.size === 0) { toast.error("Pick at least one alert"); return; }
    setRunning(true);
    try {
      const r = await api.post("/portfolio/custom", {
        alert_ids: Array.from(picked),
        window_days: 90,
      });
      setResult(r.data);
      onResult && onResult(r.data);
      toast.success("Custom scenario saved to your account");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sim failed");
    } finally {
      setRunning(false);
    }
  };

  const reset = async () => {
    setPicked(new Set());
    setResult(null);
    onResult && onResult(null);
    try { await api.delete("/portfolio/custom/saved"); } catch { /* non-fatal */ }
  };

  return (
    <div className="mt-8 p-6 rounded-lg bg-white border-2 border-dashed border-amber-400" data-testid="custom-scenario-card">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="label-uppercase text-amber-600">Custom scenario · not the published number</div>
          <h3 className="font-display font-semibold text-xl mt-1">Pick your own portfolio</h3>
          <p className="text-xs text-neutral-500 mt-1">
            Hand-pick alerts and re-run the same simulation math. This is your &quot;what if I&apos;d followed only these picks?&quot; number — not the canonical Moonbucks track record. Picks are saved to your account so this scenario is here when you come back.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={reset} className="text-xs text-neutral-500 hover:underline flex items-center gap-1" data-testid="custom-scenario-reset">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
          <button
            onClick={runSim}
            disabled={running || picked.size === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-mb-emerald text-white text-xs font-semibold disabled:opacity-40"
            data-testid="custom-scenario-run"
          >
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
            Run with {picked.size} pick{picked.size === 1 ? "" : "s"}
          </button>
        </div>
      </div>

      {(hydrating || candidates === null) && (
        <div className="text-sm text-neutral-500 py-6 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading candidates…</div>
      )}
      {!hydrating && candidates && candidates.length === 0 && (
        <div className="text-sm text-neutral-500 py-6">No alerts in the last 90 days to pick from.</div>
      )}
      {!hydrating && candidates && candidates.length > 0 && (
        <div className="mt-3 max-h-64 overflow-y-auto border border-mb-border rounded-md" data-testid="custom-scenario-candidates">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left w-8"></th>
                <th className="px-2 py-2 text-left">Symbol</th>
                <th className="px-2 py-2 text-left">Class</th>
                <th className="px-2 py-2 text-left">Entry</th>
                <th className="px-2 py-2 text-right">Result</th>
                <th className="px-2 py-2 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => {
                const isPicked = picked.has(c.id);
                const ret = c.final_return_pct;
                return (
                  <tr key={c.id} className={`border-t border-mb-border ${isPicked ? "bg-mb-emerald/5" : ""}`}>
                    <td className="px-2 py-1.5"><input type="checkbox" checked={isPicked} onChange={() => toggle(c.id)} data-testid={`custom-pick-${c.symbol}`} /></td>
                    <td className="px-2 py-1.5 font-mono-data font-semibold">{c.symbol}</td>
                    <td className="px-2 py-1.5 text-neutral-500">{c.alert_class || "steady"}</td>
                    <td className="px-2 py-1.5 font-mono-data">${c.entry_price}</td>
                    <td className={`px-2 py-1.5 text-right font-mono-data ${ret == null ? "text-neutral-400" : ret >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {ret == null ? "open" : `${ret >= 0 ? "+" : ""}${ret}%`}
                    </td>
                    <td className="px-2 py-1.5 text-neutral-500">{(c.created_at || "").slice(0, 10)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 rounded-md bg-neutral-50" data-testid="custom-scenario-result">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Final</div>
              <div className="font-mono-data text-xl font-bold">${result.final_equity?.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Return</div>
              <div className={`font-mono-data text-xl font-bold ${result.return_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                {result.return_pct >= 0 ? "+" : ""}{result.return_pct}%
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Trades</div>
              <div className="font-mono-data text-xl font-bold">{result.trades?.length || 0}</div>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-center text-amber-700">
            The chart + hero numbers above are now showing YOUR picks. Reset to see the canonical Moonbucks portfolio.
          </div>
        </div>
      )}
    </div>
  );
}
